import { useMutation, useQuery } from "@apollo/client";
import {
	Anchor,
	Badge,
	Box,
	Button,
	Card,
	Container,
	Group,
	Loader,
	Modal,
	ScrollArea,
	Stack,
	Table,
	Text,
	Title,
	Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
	IconBuilding,
	IconCheck,
	IconTrash,
	IconUser,
	IconUserShield,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import type { EmployeeGetUserQuery } from "../../../graphql/generated/types.gen";
import {
	APPROVE_WAITLIST_USER,
	EMPLOYEE_DELETE_USER,
	EMPLOYEE_GET_USER,
	EMPLOYEE_SET_USER_EMPLOYEE,
} from "../../../graphql/queries";
import { useAuthCompat } from "../../../hooks/useAuthCompat";
import { useIsMobile } from "../../../hooks/useIsMobile";

export const Route = createFileRoute("/admin/users/$userId")({
	component: AdminUserDetailPage,
});

function utcDateKey(d: Date): string {
	return d.toISOString().slice(0, 10);
}

function ActivityGrid({ activeDates }: { activeDates: string[] }) {
	const DAYS = 30;

	const { activeSet, activeDayCount } = useMemo(() => {
		const set = new Set(activeDates.map((d) => String(d).slice(0, 10)));
		return { activeSet: set, activeDayCount: set.size };
	}, [activeDates]);

	const days = useMemo(() => {
		const result = [];
		const now = new Date();
		const todayUtc = Date.UTC(
			now.getUTCFullYear(),
			now.getUTCMonth(),
			now.getUTCDate(),
		);
		for (let i = DAYS - 1; i >= 0; i--) {
			const d = new Date(todayUtc - i * 86_400_000);
			const key = utcDateKey(d);
			result.push({ key, date: d, active: activeSet.has(key) });
		}
		return result;
	}, [activeSet]);

	const dateFormatter = useMemo(
		() =>
			new Intl.DateTimeFormat(undefined, {
				weekday: "short",
				month: "short",
				day: "numeric",
				year: "numeric",
				timeZone: "UTC",
			}),
		[],
	);

	return (
		<Card shadow="sm" padding="lg" radius="md" withBorder>
			<Title order={3} mb="xs">
				Activity (Last 30 Days)
			</Title>
			<Text size="sm" c="dimmed" mb="md">
				{activeDayCount} active day{activeDayCount !== 1 && "s"} out of {DAYS}
			</Text>
			<Group gap={4} wrap="wrap">
				{days.map((day) => (
					<Tooltip
						key={day.key}
						label={`${dateFormatter.format(day.date)}${day.active ? " — Active" : ""}`}
					>
						<Box
							style={{
								width: 16,
								height: 16,
								borderRadius: 3,
								backgroundColor: day.active
									? "var(--mantine-color-green-6)"
									: "var(--mantine-color-gray-3)",
								cursor: "default",
							}}
						/>
					</Tooltip>
				))}
			</Group>
		</Card>
	);
}

function AdminUserDetailPage() {
	const { userId } = Route.useParams();
	const { user: currentUser, isLoading: authLoading } = useAuthCompat();
	const isMobile = useIsMobile();
	const navigate = useNavigate();

	const [
		deleteModalOpened,
		{ open: openDeleteModal, close: closeDeleteModal },
	] = useDisclosure(false);

	useEffect(() => {
		if (authLoading) return;
		if (!currentUser) {
			window.location.href = "/welcome";
			return;
		}
		if (!currentUser.isEmployee) {
			window.location.href = "/";
		}
	}, [currentUser, authLoading]);

	const { data, loading, refetch } = useQuery<EmployeeGetUserQuery>(
		EMPLOYEE_GET_USER,
		{
			variables: { id: userId },
			skip: !currentUser?.isEmployee,
		},
	);

	const [toggleEmployee, { loading: toggling }] = useMutation(
		EMPLOYEE_SET_USER_EMPLOYEE,
		{
			onCompleted: () => refetch(),
			onError: (error) => {
				alert(`Failed to update employee status: ${error.message}`);
			},
		},
	);

	const [deleteUser, { loading: deleting }] = useMutation(
		EMPLOYEE_DELETE_USER,
		{
			onCompleted: () => {
				navigate({ to: "/admin/users" });
			},
			onError: (error) => {
				alert(`Failed to delete user: ${error.message}`);
			},
		},
	);

	const [approveUser, { loading: approving }] = useMutation(
		APPROVE_WAITLIST_USER,
		{
			onCompleted: () => refetch(),
			onError: (error) => {
				alert(`Failed to approve user: ${error.message}`);
			},
		},
	);

	const handleApproveUser = () => {
		if (targetUser) {
			approveUser({ variables: { userId: targetUser.id } });
		}
	};

	const handleToggleEmployee = () => {
		if (targetUser) {
			toggleEmployee({
				variables: {
					userId: targetUser.id,
					isEmployee: !targetUser.isEmployee,
				},
			});
		}
	};

	const confirmDelete = () => {
		if (targetUser) {
			deleteUser({ variables: { userId: targetUser.id } });
		}
	};

	if (authLoading || !currentUser?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const targetUser = data?.employeeUser;

	if (!targetUser) {
		return (
			<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
				<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
					<div className="mb-6">
						<Anchor href="/admin/users">&larr; Back to Users</Anchor>
					</div>
					<Card shadow="sm" padding="xl" radius="md" withBorder>
						<Text size="lg" c="dimmed" ta="center">
							User not found.
						</Text>
					</Card>
				</Container>
			</div>
		);
	}

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
				<Group gap="md" mb="xs" wrap="nowrap">
					<IconUser
						size={isMobile ? 24 : 32}
						color="var(--mantine-color-blue-6)"
					/>
					<Title order={isMobile ? 2 : 1}>{targetUser.displayName}</Title>
					{targetUser.isEmployee && (
						<Badge color="violet" size="lg">
							Employee
						</Badge>
					)}
				</Group>
				<div className="mb-6">
					<Anchor href="/admin/users">&larr; Back to Users</Anchor>
				</div>

				<Stack gap="lg">
					<Card shadow="sm" padding="lg" radius="md" withBorder>
						<Title order={3} mb="md">
							User Details
						</Title>
						<Stack gap="sm">
							<Group>
								<Text fw={500} w={120}>
									Name:
								</Text>
								<Text>
									{targetUser.firstName} {targetUser.lastName}
								</Text>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Display Name:
								</Text>
								<Text>{targetUser.displayName}</Text>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Email:
								</Text>
								<Text>{targetUser.email}</Text>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Phone:
								</Text>
								<Text>{targetUser.phone || "—"}</Text>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Status:
								</Text>
								<Badge
									color={
										targetUser.betaAccessStatus === "APPROVED"
											? "green"
											: "yellow"
									}
								>
									{targetUser.betaAccessStatus}
								</Badge>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Created:
								</Text>
								<Tooltip
									label={new Date(targetUser.createdAt).toLocaleString()}
								>
									<Text style={{ cursor: "default" }}>
										{new Date(targetUser.createdAt).toLocaleDateString()}
									</Text>
								</Tooltip>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Updated:
								</Text>
								<Tooltip
									label={new Date(targetUser.updatedAt).toLocaleString()}
								>
									<Text style={{ cursor: "default" }}>
										{new Date(targetUser.updatedAt).toLocaleDateString()}
									</Text>
								</Tooltip>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									AI Cost:
								</Text>
								<Text>${(targetUser.aiCostUsd ?? 0).toFixed(2)}</Text>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Last Active:
								</Text>
								{targetUser.lastActiveAt ? (
									<Tooltip
										label={new Date(targetUser.lastActiveAt).toLocaleString()}
									>
										<Text style={{ cursor: "default" }}>
											{new Date(targetUser.lastActiveAt).toLocaleDateString()}
										</Text>
									</Tooltip>
								) : (
									<Text>Never</Text>
								)}
							</Group>
						</Stack>
					</Card>

					<Card shadow="sm" padding="lg" radius="md" withBorder>
						<Title order={3} mb="md">
							Actions
						</Title>
						<Group>
							{targetUser.betaAccessStatus === "WAITLIST" && (
								<Button
									leftSection={<IconCheck size={16} />}
									color="green"
									loading={approving}
									onClick={handleApproveUser}
								>
									Approve Waitlist
								</Button>
							)}
							<Button
								leftSection={<IconUserShield size={16} />}
								color={targetUser.isEmployee ? "gray" : "violet"}
								variant={targetUser.isEmployee ? "outline" : "filled"}
								loading={toggling}
								onClick={handleToggleEmployee}
							>
								{targetUser.isEmployee
									? "Remove Employee Status"
									: "Grant Employee Status"}
							</Button>
							<Button
								leftSection={<IconTrash size={16} />}
								color="red"
								variant="light"
								onClick={openDeleteModal}
							>
								Delete User
							</Button>
						</Group>
					</Card>

					<ActivityGrid activeDates={targetUser.activeDatesLast30Days} />

					<Card shadow="sm" padding="lg" radius="md" withBorder>
						<Title order={3} mb="md">
							Organizations ({targetUser.memberships.length})
						</Title>
						{targetUser.memberships.length === 0 ? (
							<Text c="dimmed">No organization memberships.</Text>
						) : isMobile ? (
							<Stack gap="sm">
								{targetUser.memberships.map((membership) => (
									<Card
										key={membership.organization.id}
										padding="sm"
										radius="sm"
										withBorder
									>
										<Group justify="space-between">
											<Link
												to="/admin/organizations/$organizationId"
												params={{ organizationId: membership.organization.id }}
												style={{ textDecoration: "none" }}
											>
												<Group gap="xs">
													<IconBuilding size={16} />
													<Text fw={500}>{membership.organization.name}</Text>
												</Group>
											</Link>
											<Badge size="sm">{membership.role}</Badge>
										</Group>
										<Tooltip
											label={new Date(membership.createdAt).toLocaleString()}
										>
											<Text
												size="xs"
												c="dimmed"
												mt="xs"
												style={{ cursor: "default" }}
											>
												Joined{" "}
												{new Date(membership.createdAt).toLocaleDateString()}
											</Text>
										</Tooltip>
									</Card>
								))}
							</Stack>
						) : (
							<ScrollArea>
								<Table striped highlightOnHover>
									<Table.Thead>
										<Table.Tr>
											<Table.Th>Organization</Table.Th>
											<Table.Th>Role</Table.Th>
											<Table.Th>Joined</Table.Th>
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>
										{targetUser.memberships.map((membership) => (
											<Table.Tr key={membership.organization.id}>
												<Table.Td>
													<Link
														to="/admin/organizations/$organizationId"
														params={{
															organizationId: membership.organization.id,
														}}
														style={{ textDecoration: "none" }}
													>
														<Group gap="xs">
															<IconBuilding size={16} />
															<Text size="sm">
																{membership.organization.name}
															</Text>
														</Group>
													</Link>
												</Table.Td>
												<Table.Td>
													<Badge size="sm">{membership.role}</Badge>
												</Table.Td>
												<Table.Td>
													<Tooltip
														label={new Date(
															membership.createdAt,
														).toLocaleString()}
													>
														<Text size="sm" style={{ cursor: "default" }}>
															{new Date(
																membership.createdAt,
															).toLocaleDateString()}
														</Text>
													</Tooltip>
												</Table.Td>
											</Table.Tr>
										))}
									</Table.Tbody>
								</Table>
							</ScrollArea>
						)}
					</Card>
				</Stack>
			</Container>

			<Modal
				opened={deleteModalOpened}
				onClose={closeDeleteModal}
				title="Delete User"
				centered
			>
				<Stack>
					<Text>
						Are you sure you want to delete{" "}
						<strong>{targetUser.displayName}</strong>?
					</Text>
					<Text size="sm" c="dimmed">
						This will remove the user and all their organization memberships.
						This action cannot be undone.
					</Text>
					<Group justify="flex-end" mt="md">
						<Button variant="default" onClick={closeDeleteModal}>
							Cancel
						</Button>
						<Button color="red" onClick={confirmDelete} loading={deleting}>
							Delete
						</Button>
					</Group>
				</Stack>
			</Modal>
		</div>
	);
}
